import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { formatVehicleLine } from '../../shared/utils/format-vehicle-line.util';

export interface Appointment {
  id: string;
  package_name: string;
  make: string;
  model: string;
  year: number;
  default_vehicle: boolean;
  branch: string;
  final_price: string;
  created_at: string;
  wash_type: string;
  current_period_end: string;
  appointment_status: string;
  single_list_price: string;
}

export interface AppointmentApiResponse {
  status: boolean;
  data: Appointment[];
}

/**
 * Displays a customer's appointment history with loading states, error handling, and utility formatters.
 * This standalone component fetches appointment history for a provided customerId using an ApiService,
 * exposes UI state flags (loading, error) and a collection of Appointment items, and performs internal
 * navigation to appointment details via the injected Router. It provides several helper methods to
 * derive display text, CSS status classes, formatted prices/dates, and vehicle/location information.
 * Key methods include loadAppointments (fetches data and handles success/error), onViewAppointment
 * (navigates to a detailed view), getStatusClass/getStatusText (derive status from dates),
 * getFormattedPrice/getFormattedDate (formatting helpers), getVehicleInfo/getLocationInfo,
 * getWashTypeDisplay (maps wash type keys to display suffixes), and retry (re-invokes load).
 * Lifecycle hooks:
 *   - ngOnInit: triggers loadAppointments when a valid customerId is present on initialization.
 * @component
 * @selector app-appointments
 * @example
 *   <app-appointments [customerId]="selectedCustomerId"></app-appointments>
 * @input {string} customerId - The ID of the customer whose appointment history should be loaded. When present on init, the component will automatically fetch appointments.
 * @output {none} none - This component does not emit output events. Navigation to appointment details is handled internally via the Router.
 */
@Component({
  selector: 'app-appointments',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './appointments.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./appointments.component.scss'],
})
/**
 * Appointments Component
 *
 * Displays a customer's appointment history with loading states and error handling.
 *
 * @example
 * <app-appointments [customerId]="selectedCustomerId"></app-appointments>
 */
export class AppointmentsComponent implements OnInit {
  @Input() customerId = '';

  appointments: Appointment[] = [];
  loading = false;
  error: string | null = null;

  constructor(
    private apiService: ApiService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.customerId) {
      this.loadAppointments();
    }
  }

  /**
   * Load appointment history for the current customer and update component state (appointments, loading, error).
   * @example
   * oadAppointments()
   * undefined
   * @param {void} none - No arguments.
   * @returns {void} Does not return a value; updates component state and handles loading and error states.
   **/
  loadAppointments(): void {
    if (!this.customerId) {
      this.error = 'Customer ID not found';
      return;
    }

    this.loading = true;
    this.error = null;

    this.apiService.getAppointmentHistory(this.customerId).subscribe({
      next: response => {
        if (response.status && response.data) {
          this.appointments = response.data;
        } else {
          this.error = 'Failed to load appointments';
        }
        this.loading = false;
      },
      error: error => {
        console.error('Error loading appointments:', error);
        this.error = 'Failed to load appointments. Please try again.';
        this.loading = false;
      },
    });
  }

  onViewAppointment(appointmentId: string): void {
    console.log('Viewing appointment:', appointmentId);
    // Implement view appointment logic here
    this.router.navigate(['/appointments'], { queryParams: { appointmentId: appointmentId } });
  }

  getStatusClass(appointment: Appointment): string {
    const endDate = new Date(appointment.current_period_end);
    const now = new Date();
    return endDate > now ? 'upcoming' : 'completed';
  }

  getStatusText(appointment: Appointment): string {
    const endDate = new Date(appointment.current_period_end);
    const now = new Date();
    return endDate > now ? 'UPCOMING' : 'COMPLETED';
  }

  getFormattedPrice(appointment: Appointment): string {
    const price = parseFloat(appointment.single_list_price);
    return `$${price.toFixed(2)}`;
  }

  getFormattedDate(appointment: Appointment): string {
    const date = new Date(appointment.created_at);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  getVehicleInfo(appointment: Appointment): string {
    return formatVehicleLine(appointment.year, appointment.make, appointment.model);
  }

  getLocationInfo(appointment: Appointment): string {
    return appointment.branch;
  }

  /**
   * Returns a display suffix for a wash type (e.g., '/month', '/week').
   * @example
   * etWashTypeDisplay('monthly_membership')
   * '/month'
   * @param {{string}} {{washType}} - The wash type identifier.
   * @returns {{string}} Return suffix to display for the given wash type.
   **/
  getWashTypeDisplay(washType: string): string {
    switch (washType) {
      case 'monthly_membership':
        return '/month';
      case 'weekly_membership':
        return '/week';
      case 'daily_membership':
        return '/day';
      case 'one_time':
        return '/wash';
      default:
        return '/wash';
    }
  }

  retry(): void {
    this.loadAppointments();
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
